import { useState } from "react";
import { PasswordInput } from "../../../../Components/ui/input";
import PrimaryButton from "../components/PrimaryButton";
import { CardSubtitle, CardTitle } from "../components/AuthCard";
import { CheckIcon, ShieldIcon } from "../components/Icons";
import { PASSWORD_RULES } from "../utils";

const PasswordField = ({ label, name, value, onChange, errorMessage }) => (
  <div>
    <label
      htmlFor={name}
      className="font-geomanistBold text-xs leading-4 text-blackShade mb-[5px] pl-1 block"
    >
      {label}
    </label>
    <PasswordInput
      id={name}
      name={name}
      value={value}
      onChange={onChange}
      error={errorMessage}
      autoComplete="new-password"
      className="font-geomanistBold text-[16px]"
    />
    {errorMessage && (
      <p className="mt-1 mb-0 font-geomanistRegular text-xs leading-4 text-failed">
        {errorMessage}
      </p>
    )}
  </div>
);

// A10 / A11 — mandatory on first 2FA login
const SetNewPassword = ({ header, passwordError, onClearError, onSubmit }) => {
  const [form, setForm] = useState({ newPassword: "", confirmPassword: "" });
  const [loading, setLoading] = useState(false);

  const { newPassword, confirmPassword } = form;

  const ruleResults = PASSWORD_RULES.map((rule) => {
    const passed = rule.test(newPassword);
    return { ...rule, passed, failed: !!newPassword && !passed };
  });

  const allRulesPass = ruleResults.every((rule) => rule.passed);
  const mismatch = !!confirmPassword && confirmPassword !== newPassword;
  const canSubmit = allRulesPass && !!confirmPassword && !mismatch && !loading;

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    onClearError?.();
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setLoading(true);
    await onSubmit({ newPassword });
    setLoading(false);
  };

  return (
    <>
      {header}
      <span className="inline-flex items-center gap-1.5 rounded-full bg-[#E3F7EA] px-2.5 py-1 mb-3 font-geomanistBold text-[11px] leading-4 text-success">
        <ShieldIcon className="w-3.5 h-3.5" />
        Email &amp; mobile verified
      </span>
      <CardTitle>Set a new password</CardTitle>
      <CardSubtitle>For your security, change your password before continuing.</CardSubtitle>

      <div className="mt-5">
        <PasswordField
          label="New password"
          name="newPassword"
          value={newPassword}
          onChange={handleChange}
        />
        <ul className="grid grid-cols-2 gap-x-3 gap-y-1 list-none p-0 mt-2 mb-0">
          {ruleResults.map((rule) => (
            <li
              key={rule.key}
              className={`flex items-center gap-1.5 font-geomanistRegular text-[11px] leading-4
                ${rule.passed ? "text-success" : rule.failed ? "text-failed" : "text-geomanistgray"}`}
            >
              {rule.passed ? (
                <CheckIcon className="w-3 h-3" />
              ) : (
                <span className="w-3 h-3 flex items-center justify-center">
                  <span className="w-1 h-1 rounded-full bg-current" />
                </span>
              )}
              {rule.label}
            </li>
          ))}
        </ul>
        {passwordError && (
          <p className="mt-1 mb-0 font-geomanistRegular text-xs leading-4 text-failed">
            {passwordError}
          </p>
        )}
      </div>

      <div className="mt-4">
        <PasswordField
          label="Confirm new password"
          name="confirmPassword"
          value={confirmPassword}
          onChange={handleChange}
          errorMessage={mismatch ? "Passwords don't match." : ""}
        />
      </div>

      <div className="mt-5">
        <PrimaryButton
          title="Update password"
          onClick={handleSubmit}
          loading={loading}
          disabled={!canSubmit}
        />
      </div>
    </>
  );
};

export default SetNewPassword;
